/**
 * HTTP Service Wrapper
 * Wraps the generic HTTP service from lib/ui with TFS-specific configuration
 * Includes 401 unauthorized handling to redirect to login
 * Includes X-Locale header for i18n
 */

import { storageService } from './StorageService';
import { config } from '@/config';
import { httpService as genericHttpService } from '@/lib/ui/services/HttpService';
import type { ApiError } from '@/lib/ui/types';
import type { MaintenanceState } from '@/types';
import { useAuthStore } from '@/stores/auth';
import { startOAuthLogin } from '@/services/OAuthLoginNavigation';
import { i18n } from '@/plugins/i18n';

const AUTH_SERVICE_ACCESS_TOKEN_STORAGE_KEY = 'auth_service_access_token';
const OAUTH_LOCAL_TOKEN_STORAGE_KEY = 'oauth_local_token';
const MAINTENANCE_STATE_STORAGE_KEY = 'maintenance_state';
const MAINTENANCE_RETURN_TO_STORAGE_KEY = 'maintenance_return_to';
const MAINTENANCE_ROUTE_PATH = '/maintenance';
const MAINTENANCE_POLL_INTERVAL_MS = 15000;
const MAINTENANCE_STATUS_ENDPOINT = '/health';
const OAUTH_EXCLUDED_401_ENDPOINTS = new Set([
  '/auth/oauth/callback',
  '/auth/oauth/redirect',
]);

type ExtendedApiErrorData = ApiError['data'] & {
  error?: string;
  retry_after?: number | null;
  maintenance_message?: string;
};

type ExtendedApiError = Omit<ApiError, 'data'> & {
  data: ExtendedApiErrorData;
};

let maintenancePoller: ReturnType<typeof setInterval> | null = null;

function getCurrentPathWithQuery(): string {
  if (typeof window === 'undefined') return '/';
  return `${window.location.pathname}${window.location.search}`;
}

function getMaintenanceState(): MaintenanceState | null {
  return storageService.get<MaintenanceState>(MAINTENANCE_STATE_STORAGE_KEY);
}

function setMaintenanceState(state: MaintenanceState): void {
  storageService.set(MAINTENANCE_STATE_STORAGE_KEY, state);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('maintenance-mode-changed', { detail: state }));
  }
}

function clearMaintenanceState(): void {
  storageService.remove(MAINTENANCE_STATE_STORAGE_KEY);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('maintenance-mode-changed', { detail: null }));
  }
}

function isMaintenanceActive(): boolean {
  const state = getMaintenanceState();
  return state?.active === true;
}

function isBypassedDuringMaintenance(endpoint: string): boolean {
  const normalizedEndpoint = normalizeEndpoint(endpoint);
  return normalizedEndpoint.startsWith(MAINTENANCE_STATUS_ENDPOINT)
    || normalizedEndpoint.startsWith('/auth/')
    || normalizedEndpoint.startsWith('/oauth/');
}

function isMaintenanceModeError(error: ApiError): error is ExtendedApiError {
  const apiError = error as ExtendedApiError;
  return apiError.status === 503 && apiError.data?.error === 'maintenance_mode';
}

function isMaintenanceNoAccessError(error: ApiError): error is ExtendedApiError {
  const apiError = error as ExtendedApiError;
  return apiError.status === 503 && apiError.data?.error === 'maintenance_mode_no_access';
}

function getMaintenanceMessage(data: ExtendedApiErrorData): string {
  return data.maintenance_message
    || data.message
    || 'Service temporarily unavailable due to maintenance';
}

function buildMaintenanceError(): ExtendedApiError {
  const state = getMaintenanceState();

  return {
    status: 503,
    data: {
      error: 'maintenance_mode',
      message: state?.message ?? 'Service temporarily unavailable due to maintenance',
      maintenance_message: state?.message,
      retry_after: state?.retryAfter,
    },
  };
}

async function probeMaintenanceStatus(): Promise<boolean> {
  try {
    const response = await genericHttpService.public.get<{ status?: string; maintenance_active?: boolean }>(
      MAINTENANCE_STATUS_ENDPOINT,
      undefined,
      getLocaleHeader(),
    );

    return response.status === 'ok' && response.maintenance_active === false;
  } catch {
    return false;
  }
}

async function tryRecoverFromMaintenance(): Promise<boolean> {
  const recovered = await probeMaintenanceStatus();
  if (!recovered) {
    return false;
  }

  clearMaintenanceState();
  stopMaintenancePolling();

  if (typeof window !== 'undefined' && window.location.pathname === MAINTENANCE_ROUTE_PATH) {
    const returnTo = sessionStorage.getItem(MAINTENANCE_RETURN_TO_STORAGE_KEY);
    sessionStorage.removeItem(MAINTENANCE_RETURN_TO_STORAGE_KEY);
    window.location.href = returnTo && returnTo !== MAINTENANCE_ROUTE_PATH ? returnTo : '/';
  }

  return true;
}

function startMaintenancePolling(): void {
  if (maintenancePoller || typeof window === 'undefined') {
    return;
  }

  maintenancePoller = setInterval(() => {
    void tryRecoverFromMaintenance();
  }, MAINTENANCE_POLL_INTERVAL_MS);
}

function stopMaintenancePolling(): void {
  if (!maintenancePoller) {
    return;
  }

  clearInterval(maintenancePoller);
  maintenancePoller = null;
}

function redirectToMaintenance(): void {
  if (typeof window === 'undefined') return;

  if (window.location.pathname !== MAINTENANCE_ROUTE_PATH) {
    sessionStorage.setItem(MAINTENANCE_RETURN_TO_STORAGE_KEY, getCurrentPathWithQuery());
    window.location.href = MAINTENANCE_ROUTE_PATH;
  }
}

function activateMaintenanceMode(error: ExtendedApiError): void {
  const state: MaintenanceState = {
    active: true,
    message: getMaintenanceMessage(error.data),
    retryAfter: typeof error.data.retry_after === 'number' ? error.data.retry_after : null,
    detectedAt: new Date().toISOString(),
  };

  setMaintenanceState(state);
  startMaintenancePolling();
  redirectToMaintenance();
}

// Get current locale for X-Locale header
function getLocaleHeader(): Record<string, string> {
  // Only send X-Locale when user doesn't have a persisted preference
  // This is determined by checking if the auth store has a user with locale
  try {
    const authStore = useAuthStore();
    // If user is authenticated and has a locale set, don't send X-Locale
    // Backend will use user->locale
    if (authStore.user?.locale) {
      return {};
    }
  } catch {
    // If store not available (e.g., during initialization), use current i18n locale
  }

  // Send X-Locale for anonymous users or users without persisted locale
  return { 'X-Locale': i18n.global.locale.value };
}

// Initialize the generic service with TFS config
function init(): void {
  genericHttpService.init({
    apiBase: config.apiBase,
    storage: {
      get: <T>(key: string) => storageService.get<T>(key),
      set: <T>(key: string, value: T) => storageService.set(key, value),
      remove: (key: string) => storageService.remove(key),
    },
  });

  if (isMaintenanceActive()) {
    startMaintenancePolling();
  }
}

// Handle 401 errors by clearing auth and redirecting to login
function handleUnauthorizedError(): void {
  try {
    const authStore = useAuthStore();
    authStore.clearAuth();
    
    // Avoid re-triggering OAuth from the callback route.
    const currentPath = window.location.pathname;
    if (!currentPath.includes('/auth/callback')) {
      const currentUrl = window.location.pathname + window.location.search;
      void startOAuthLogin(currentUrl);
    }
  } catch (_error) {
    // Silently handle case where Pinia store is not available (e.g., in tests)
    console.warn('Auth store not available during 401 handling');
  }
}

function normalizeEndpoint(endpoint: string): string {
  return endpoint.startsWith('/api/') ? endpoint.slice(4) : endpoint;
}

function shouldHandleUnauthorizedError(endpoint: string, requiresAuth: boolean): boolean {
  if (!requiresAuth) {
    return false;
  }

  const normalizedEndpoint = normalizeEndpoint(endpoint);
  return !OAUTH_EXCLUDED_401_ENDPOINTS.has(normalizedEndpoint)
    && !/^\/videos\/[^/]+\/playback$/.test(normalizedEndpoint);
}

// Wrapper function that adds 401 handling
async function wrapRequest<T>(
  endpoint: string,
  requestFn: () => Promise<T>,
  options?: { requiresAuth?: boolean },
): Promise<T> {
  const requiresAuth = options?.requiresAuth ?? true;

  if (isMaintenanceActive() && !isBypassedDuringMaintenance(endpoint)) {
    redirectToMaintenance();
    throw buildMaintenanceError();
  }

  try {
    return await requestFn();
  } catch (error) {
    const apiError = error as ApiError;
    if (isMaintenanceModeError(apiError)) {
      activateMaintenanceMode(apiError);
    }

    if (apiError.status === 401 && shouldHandleUnauthorizedError(endpoint, requiresAuth)) {
      handleUnauthorizedError();
    }

    throw error;
  }
}

// Wrapper that delegates to generic service with 401 handling and X-Locale header
export const httpService = {
  getWithCredentials: <T>(endpoint: string, headers?: Record<string, string>) =>
    wrapRequest(endpoint, () => genericHttpService.getWithCredentials<T>(endpoint, { ...getLocaleHeader(), ...headers })),
  get: <T>(endpoint: string, params?: Record<string, string>, headers?: Record<string, string>) =>
    wrapRequest(endpoint, () => genericHttpService.get<T>(endpoint, params, { ...getLocaleHeader(), ...headers })),
  post: <T>(endpoint: string, body?: unknown, headers?: Record<string, string>) =>
    wrapRequest(endpoint, () => genericHttpService.post<T>(endpoint, body, { ...getLocaleHeader(), ...headers })),
  put: <T>(endpoint: string, body?: unknown, headers?: Record<string, string>) =>
    wrapRequest(endpoint, () => genericHttpService.put<T>(endpoint, body, { ...getLocaleHeader(), ...headers })),
  patch: <T>(endpoint: string, body?: unknown, headers?: Record<string, string>) =>
    wrapRequest(endpoint, () => genericHttpService.patch<T>(endpoint, body, { ...getLocaleHeader(), ...headers })),
  delete: <T>(endpoint: string, headers?: Record<string, string>) =>
    wrapRequest(endpoint, () => genericHttpService.delete<T>(endpoint, { ...getLocaleHeader(), ...headers })),
  deleteWithBody: <T>(endpoint: string, body?: unknown, headers?: Record<string, string>) =>
    wrapRequest(endpoint, () => genericHttpService.deleteWithBody<T>(endpoint, body, { ...getLocaleHeader(), ...headers })),
  public: {
    get: <T>(endpoint: string, params?: Record<string, string>, headers?: Record<string, string>) =>
      wrapRequest(
        endpoint,
        () => genericHttpService.public.get<T>(endpoint, params, { ...getLocaleHeader(), ...headers }),
        { requiresAuth: false },
      ),
    post: <T>(endpoint: string, body?: unknown, headers?: Record<string, string>) =>
      wrapRequest(
        endpoint,
        () => genericHttpService.public.post<T>(endpoint, body, { ...getLocaleHeader(), ...headers }),
        { requiresAuth: false },
      ),
  },
  postWithProgress: <T>(
    endpoint: string,
    data: FormData,
    onProgress?: (percent: number) => void,
    headers?: Record<string, string>
  ): Promise<T> =>
    wrapRequest(endpoint, () => genericHttpService.postWithProgress<T>(endpoint, data, onProgress, { ...getLocaleHeader(), ...headers })),
  setAuthToken: (token: string | null): void => {
    genericHttpService.setAuthToken(token);
    // Also update storage directly for backward compatibility
    if (token) storageService.set('auth_token', token);
    else storageService.remove('auth_token');
  },
  getAuthToken: genericHttpService.getAuthToken.bind(genericHttpService),
  setAuthServiceAccessToken: (token: string | null): void => {
    if (token) {
      storageService.set(AUTH_SERVICE_ACCESS_TOKEN_STORAGE_KEY, token);
      return;
    }

    storageService.remove(AUTH_SERVICE_ACCESS_TOKEN_STORAGE_KEY);
  },
  getAuthServiceAccessToken: (): string | null => {
    return storageService.get<string>(AUTH_SERVICE_ACCESS_TOKEN_STORAGE_KEY);
  },
  setOAuthLocalToken: (token: string | null): void => {
    if (token) {
      storageService.set(OAUTH_LOCAL_TOKEN_STORAGE_KEY, token);
      return;
    }

    storageService.remove(OAUTH_LOCAL_TOKEN_STORAGE_KEY);
  },
  getOAuthLocalToken: (): string | null => {
    return storageService.get<string>(OAUTH_LOCAL_TOKEN_STORAGE_KEY);
  },
  getMaintenanceState: (): MaintenanceState | null => {
    return getMaintenanceState();
  },
  isMaintenanceActive: (): boolean => {
    return isMaintenanceActive();
  },
  handleMaintenanceError: (error: ApiError): boolean => {
    if (isMaintenanceModeError(error)) {
      activateMaintenanceMode(error);
      return true;
    }

    if (isMaintenanceNoAccessError(error)) {
      return false;
    }

    return false;
  },
  checkMaintenanceRecovery: async (): Promise<boolean> => {
    return await tryRecoverFromMaintenance();
  },
  clearMaintenanceState: (): void => {
    clearMaintenanceState();
    stopMaintenancePolling();
  },
  init,
};

// Re-export types
export type { ApiError };
